import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { styled } from 'goober';
import { useSearchParams } from 'react-router-dom';

import { Loader } from '@/components/loader/loader';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useCurrentUser } from '@/user/use-current-user.query';

import type { FeedTab } from './feed.state';
import { feedActiveTabState } from './feed.state';
import { FeedTabContent } from './feed-tab-content';
import { useDefaultFeedTab, useFeedTabIds } from './use-feed-tabs-config';

export const FeedContent = () => {
  const [activeTab, setActiveTab] = useAtom(feedActiveTabState);
  const resetActiveTab = useResetAtom(feedActiveTabState);
  const defaultTab = useDefaultFeedTab();
  const allowedTabs = useFeedTabIds();
  const { isFetched } = useCurrentUser();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    const tab = searchParams.get('tab') as FeedTab | null;

    if (tab) {
      if (!allowedTabs.includes(tab)) {
        setActiveTab(defaultTab);
        setSearchParams({ tab: defaultTab });
        return;
      }

      setActiveTab(tab);
      return;
    }

    setActiveTab(defaultTab);
    setSearchParams({ tab: defaultTab });
  }, [
    allowedTabs,
    defaultTab,
    isFetched,
    searchParams,
    setActiveTab,
    setSearchParams,
  ]);

  useEffect(() => {
    return () => {
      resetActiveTab();
    };
  }, [resetActiveTab]);

  if (!isFetched) {
    return (
      <Container
        alignItems="center"
        direction="column"
        flex={1}
        justify="center"
      >
        <Loader />
      </Container>
    );
  }

  return (
    <Container direction="column" flex={1}>
      <FeedTabContent tab={activeTab} />
    </Container>
  );
};

const Container = styled(Flexbox)`
  min-height: 0;
  overflow: hidden;
`;
